import {
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	CircularProgress,
	Grid,
	Hidden,
	IconButton,
	Link,
	Tooltip,
	Typography,
} from '@material-ui/core';
import dayjs from 'dayjs';
import * as _ from 'lodash';
import * as React from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import { Column, UseTableCellProps } from 'react-table';

import { AccountStatusEnum, PredefinedFiltersEnum, SnackBarVariants } from '@app/app.enum';
import { history } from '@app/app.routes';
import store from '@app/app.store';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import CustomDatePicker from '@app/components/shared/custom-date-picker.component';
import Icon from '@app/components/shared/icon.component';
import PredefinedFilters from '@app/components/shared/predefined-filters.component';
import Table from '@app/components/shared/table.component';
import * as constants from '@app/constants';
import * as models from '@app/models';
import { initialReportBalanceHistoryComponentState } from '@app/models/report.models';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import Axios from 'axios';

const intlService: IntlService = Injector.get(IntlService);
const accountService: AccountService = Injector.get(AccountService);
const sharedService: SharedService = Injector.get(SharedService);
const pageSize = 20;

class ReportBalanceHistoryComponent extends React.Component<
	models.IBalanceHistoryComponentProps,
	models.IBalanceHistoryComponentState
> {
	public static getDerivedStateFromProps(
		props: models.IBalanceHistoryComponentProps,
		state: models.IBalanceHistoryComponentState
	): models.IBalanceHistoryComponentState | null {
		if (props.loading !== undefined && props.loading !== state.dataLoading) {
			return {
				...state,
				dataLoading: props.loading,
			};
		}
		return null;
	}

	private isAccountClosed = store.getState().authentication.status === AccountStatusEnum.Closed;

	constructor(props: models.IBalanceHistoryComponentProps) {
		super(props);
		this.state = initialReportBalanceHistoryComponentState(props?.rows, false);
	}

	public componentDidMount() {
		const startDate = this.props.startDate ? this.props.startDate : this.state.startDate;
		const endDate = this.props.endDate ? this.props.endDate : this.state.endDate;

		if (!this.props.contained) {
			this.getAccountDetails();
			this.fetchData(startDate, endDate, this.state.currentPage);
		}
	}

	public componentDidUpdate(
		prevProps: models.IBalanceHistoryComponentProps,
		prevState: models.IBalanceHistoryComponentState
	) {
		if (this.shouldFetchData(prevState)) {
			this.fetchData(this.state.startDate, this.state.endDate, this.state.currentPage);
		}

		if (prevState.currentPage !== this.state.currentPage && !!this.props.updateCurrentPage) {
			this.props.updateCurrentPage(this.state.currentPage);
		}

		if (prevProps.startDate !== this.props.startDate || prevProps.endDate !== this.props.endDate) {
			this.props.updateCurrentPage(1);
			this.setState({ ...this.state, currentPage: 1 });
		}

		if (this.props.rows !== prevProps.rows) {
			this.setState({
				currentPage: this.state.currentPage,
				results: this.props.rows,
				totalResults: this.props.totalResults,
				totalPages: this.props.totalPages,
			});
		}
	}

	public fetchData(startDate: Date, endDate: Date, page: number) {
		this.loadData(page, startDate, endDate);
	}

	public render() {
		const filterRow = this.props.contained ? null : this.getFilterRow();

		const cardHeading: string = this.props.contained && this.props.heading ? this.props.heading : null;
		return (
			<React.Fragment>
				<Grid container={true} spacing={2} className='balance-history'>
					<Grid item={true} lg={12} md={12} sm={12} xs={12}>
						<Card>
							{cardHeading && (
								<CardHeader className='pb-0' title={cardHeading} titleTypographyProps={{ variant: 'body2' }} />
							)}
							<CardContent>
								{filterRow}
								<Hidden only={['md', 'sm', 'xs']}>
									<Grid container={true}>
										<Table<models.IAccountTransaction>
											columns={this.getGridColumns()}
											contained={this.props.contained}
											data={this.state.results}
											dataLoading={this.state.dataLoading}
											currentPage={this.state.currentPage}
											pageJump={this.state.pageJump}
											getCurrentPage={() => this.state.currentPage}
											getPageJump={() => this.state.pageJump}
											totalPages={this.state.totalPages}
											pageSize={this.props.pageSize ?? pageSize}
											totalRecords={this.state.totalResults}
											siblingCount={this.props.siblingCount}
											onPageChange={(e: object, page: number) =>
												this.callAfterCancellingActiveRequests(() => this.setState({ currentPage: page }))()
											}
											setCurrentPage={(page: number) => {
												this.setState({
													currentPage: page,
												});
											}}
											setPageJump={(page: number) => {
												this.setState({
													pageJump: page,
												});
											}}
										/>
									</Grid>
								</Hidden>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</React.Fragment>
		);
	}

	private getGridColumns = (): Array<Column<models.IAccountTransaction>> => {
		const columns: Array<Column<models.IAccountTransaction>> = [
			{
				accessor: 'transactionDate',
				Header: intlService.GetFormattedMessage('app.balance-history.date-table-header'),
				Cell: (tableData: UseTableCellProps<models.IAccountTransaction>) => (
					<FormattedDate {...constants.dateFormatOptions} value={sharedService.getNewDateIfNotExist(tableData.value)} />
				),
				disableSortBy: true,
			},
			{
				accessor: 'comments',
				Header: intlService.GetFormattedMessage('app.balance-history.description-table-header'),
				Cell: (tableData: UseTableCellProps<models.IAccountTransaction>) => <>{tableData.value || '-'}</>,
				disableSortBy: true,
			},
			{
				accessor: 'amount',
				Header: intlService.GetFormattedMessage('app.balance-history.amount-table-header'),
				Cell: (tableData: UseTableCellProps<models.IAccountTransaction>) => {
					return (
						<>
							<Typography variant='body2'>
								$
								<FormattedNumber value={tableData.value} maximumFractionDigits={2} minimumFractionDigits={2} />
							</Typography>
						</>
					);
				},
				disableSortBy: true,
			},
		];

		if (!this.props.contained) {
			columns.push({
				accessor: 'transactionId',
				Cell: (tableData: UseTableCellProps<models.IAccountTransaction>) => {
					const onClickDownloadButton = async () => {
						try {
							const res = await accountService.downloadReceipt(tableData.value);

							const fileName = sharedService.parseContentDisposition(
								res.request.getResponseHeader(constants.contentDispositionHeader)
							);
							sharedService.downloadFile(res.data, fileName);
						} catch (error) {
							throw error;
						}
					};

					return (
						<Link underline='none' className={`pointer action-download`} onClick={onClickDownloadButton}>
							<FormattedMessage id='app.balance-history.download-receipt-label' />
						</Link>
					);
				},
				disableSortBy: true,
			});
		}

		return columns;
	};

	private loadData = async (requestedPage: number, startDate: Date, endDate: Date) => {
		try {
			let pageToken = sharedService.generatePaginationToken(requestedPage - 2);

			if (requestedPage === 0) {
				pageToken = undefined;
			}

			let balanceHistory: models.IPagedResult<models.IAccountTransaction> = {
				results: [],
				nextPage: '',
				previousPage: '',
				pageSize: 0,
				totalResults: 0,
				totalPages: 0,
			};
			this.setState({
				dataLoading: true,
				...balanceHistory,
			});
			if (!this.props.contained) {
				balanceHistory = await accountService.getAccountBalanceHistory(
					pageToken,
					startDate,
					endDate,
					this.state.selectedEventTypeFilterValues,
					this.state.selectedTransactionTypeFilterValues,
					this.props.axiosCancellationToken
				);
			}

			this.setState({
				...balanceHistory,
				dataLoading: false,
			});
		} catch (e) {
			if (!Axios.isCancel(e)) {
				this.props.openSnackBar(sharedService.parseError(e.data), SnackBarVariants.Error);
			}
		}
	};

	private getFilterRow() {
		return (
			<>
				<Grid container={true} spacing={2}>
					<Grid item={true} lg={7} md={12} sm={12} xs={12}>
						<Grid container={true} justifyContent='flex-start' spacing={2}>
							<Grid item={true} lg={4} md={4} sm={12} xs={12}>
								<PredefinedFilters
									startDate={this.state.startDate}
									endDate={this.state.endDate}
									onDateChange={this.callAfterCancellingActiveRequests(this.loadDataForPredefinedFilters)}
								/>
							</Grid>
							<Grid item={true} lg={4} md={4} sm={12} xs={12}>
								<CustomDatePicker
									label={intlService.GetFormattedMessage('app.balance-history.start-date-label')}
									maxDate={this.state.endDate}
									disabled={this.state.disableDatePickerFilter}
									value={this.state.startDate}
									onChange={(date: dayjs.Dayjs) => {
										const composedFunc = this.callAfterCancellingActiveRequests(() =>
											this.setState({
												results: [],
												startDate: date.toDate(),
												currentPage: 1,
											})
										);
										composedFunc();
									}}
								/>
							</Grid>
							<Grid item={true} lg={4} md={4} sm={12} xs={12}>
								<CustomDatePicker
									label={intlService.GetFormattedMessage('app.balance-history.end-date-label')}
									minDate={this.state.startDate}
									disabled={this.state.disableDatePickerFilter}
									value={this.state.endDate}
									onChange={(date: dayjs.Dayjs) => {
										const composedFunc = this.callAfterCancellingActiveRequests(() =>
											this.setState({
												results: [],
												endDate: date.toDate(),
												currentPage: 1,
											})
										);
										composedFunc();
									}}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item={true} lg={5} md={12} sm={12} xs={12}>
						<Grid container={true}>
							<Grid item={true} lg={1} md={4} sm={12} xs={12}>
								<Hidden only={['md', 'sm', 'xs']}>
									<Box height='100%' alignItems='center' display='flex'>
										<Tooltip title={intlService.GetFormattedMessage('app.common.export')}>
											<IconButton onClick={() => this.exportReport()}>
												<Icon name='tray-arrow-down' />
											</IconButton>
										</Tooltip>
									</Box>
								</Hidden>
								<Hidden only={['xl', 'lg']}>
									<Button variant='outlined' color='primary' onClick={() => this.exportReport()}>
										<Icon name='tray-arrow-down' />
										<span className='pl-1'>{intlService.GetFormattedMessage('app.common.export')}</span>
									</Button>
								</Hidden>
							</Grid>
							<Hidden only={['sm', 'xs']}>
								<Grid item={true} lg={11} md={8} sm={12} xs={12}>
									<Grid container={true}>
										<Grid item={true} lg={12} md={12} sm={12} xs={12}>
											<Grid container={true} justifyContent='flex-end'>
												<Typography variant='h6' id='BalanceHistoryCurrentBalance'>
													{intlService.GetFormattedMessage('app.balance-history.current-balance')}:{' '}
													{this.state.currentBalance !== null ? (
														<>
															$
															<FormattedNumber
																value={this.state.currentBalance}
																maximumFractionDigits={2}
																minimumFractionDigits={2}
															/>
														</>
													) : (
														<span className='circular-progress-position'>
															<CircularProgress size={18} />
														</span>
													)}
												</Typography>
											</Grid>
										</Grid>
										{!this.isAccountClosed ? (
											<Grid item={true} lg={12} md={12} sm={12} xs={12}>
												<Grid container={true} justifyContent='flex-end'>
													<Link
														component='button'
														onClick={() => {
															history.push(models.Module.Billing.url);
														}}
													>
														{intlService.GetFormattedMessage('app.balance-history.add-balance')}
													</Link>
												</Grid>
											</Grid>
										) : null}
									</Grid>
								</Grid>
							</Hidden>
						</Grid>
					</Grid>
				</Grid>
			</>
		);
	}

	private async getAccountDetails() {
		try {
			const result = await accountService.getAccountDetails(this.props.axiosCancellationToken);
			this.setState({
				currentBalance: result.balance,
			});
		} catch (e) {
			if (!Axios.isCancel(e)) {
				this.props.openSnackBar(sharedService.parseError(e.data), SnackBarVariants.Error);
			}
		}
	}

	private async exportReport() {
		try {
			const res = await accountService.exportBalanceHistoryReport(
				this.state.startDate,
				this.state.endDate,
				this.state.selectedEventTypeFilterValues,
				this.state.selectedTransactionTypeFilterValues,
				this.props.axiosCancellationToken
			);
			const fileName = sharedService.parseContentDisposition(
				res.request.getResponseHeader(constants.contentDispositionHeader)
			);
			sharedService.downloadFile(res.data, fileName);
		} catch (e) {
			if (!Axios.isCancel(e)) {
				this.props.openSnackBar(sharedService.parseError(e.data), SnackBarVariants.Error);
			}
		}
	}

	private loadDataForPredefinedFilters = (startDate: Date, endDate: Date, filterValue: PredefinedFiltersEnum) => {
		this.setState({
			results: [],
			disableDatePickerFilter: filterValue !== PredefinedFiltersEnum.Custom,
			startDate,
			endDate,
			currentPage: 1,
		});
	};

	private shouldFetchData = (prevState: models.IBalanceHistoryComponentState): boolean => {
		return (
			(prevState.startDate !== this.state.startDate ||
				prevState.endDate !== this.state.endDate ||
				prevState.currentPage !== this.state.currentPage) &&
			!this.props.isRequestCancelled
		);
	};

	private callAfterCancellingActiveRequests = (callback: (...args: any) => void) => {
		return async (...args: any) => {
			await this.props.cancelActiveRequests();
			callback(...args);
		};
	};
}

export default withAxiosCancellation(withSnackBar(ReportBalanceHistoryComponent));



// WEBPACK FOOTER //
// ./src/components/report/balance-history/balance-history.component.tsx